import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, Theme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Проверяваме localStorage за запазена тема
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });

  // Запазваме избора в localStorage
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  // Създаваме темата динамично
  const theme: Theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: {
            main: isDarkMode ? '#2196f3' : '#1976d2',
            light: isDarkMode ? '#64b5f6' : '#42a5f5',
            dark: isDarkMode ? '#1976d2' : '#1565c0',
            contrastText: '#ffffff',
          },
          secondary: {
            main: isDarkMode ? '#f06292' : '#dc004e',
            light: isDarkMode ? '#f48fb1' : '#f50057',
            dark: isDarkMode ? '#c2185b' : '#c51162',
            contrastText: '#ffffff',
          },
          success: {
            main: isDarkMode ? '#66bb6a' : '#2e7d32',
            light: isDarkMode ? '#81c784' : '#4caf50',
            dark: isDarkMode ? '#388e3c' : '#1b5e20',
            contrastText: '#ffffff',
          },
          error: {
            main: isDarkMode ? '#f44336' : '#d32f2f',
            light: isDarkMode ? '#e57373' : '#f44336',
            dark: isDarkMode ? '#d32f2f' : '#c62828',
            contrastText: '#ffffff',
          },
          warning: {
            main: isDarkMode ? '#ffa726' : '#ed6c02',
            light: isDarkMode ? '#ffb74d' : '#ff9800',
            dark: isDarkMode ? '#f57c00' : '#e65100',
            contrastText: '#ffffff',
          },
          info: {
            main: isDarkMode ? '#29b6f6' : '#0288d1',
            light: isDarkMode ? '#4fc3f7' : '#03a9f4',
            dark: isDarkMode ? '#0288d1' : '#01579b',
            contrastText: '#ffffff',
          },
          background: {
            default: isDarkMode ? '#121212' : '#f5f5f5',
            paper: isDarkMode ? '#1e1e1e' : '#ffffff',
          },
          text: {
            primary: isDarkMode ? '#ffffff' : '#000000',
            secondary: isDarkMode ? '#b0b0b0' : '#666666',
          },
        },
        typography: {
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
        },
      }),
    [isDarkMode]
  );

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
